import { useParams, NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Code2,
  Settings as SettingsIcon,
  CreditCard,
  Cog,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useState } from "react";
import { cn } from "../ui/utils";
import { useWebsite } from "../../contexts/WebsiteContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Button } from "../ui/button";

const menuItems = [
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    path: "/dashboard",
    requiresWebsite: false,
  },
  {
    icon: Code2,
    label: "Widget Integration",
    path: "/widget-integration",
    requiresWebsite: true,
  },
  {
    icon: SettingsIcon,
    label: "Widget Settings",
    path: "/widget-settings",
    requiresWebsite: true,
  },
  // { icon: FileText, label: 'Pages', path: '/pages', requiresWebsite: true },
  // { icon: FileWarning, label: 'Unreachable Pages', path: '/unreachable-pages', requiresWebsite: true },
  // { icon: File, label: 'Documents', path: '/documents', requiresWebsite: true },
  // {
  //   icon: AlertTriangle,
  //   label: 'Automated Tests',
  //   path: '/automated-tests',
  //   requiresWebsite: true,
  // },
  {
    icon: CreditCard,
    label: "Manage Plan",
    path: "/manage-plan",
    requiresWebsite: true,
  },
  { icon: Cog, label: "Settings", path: "/settings", requiresWebsite: false },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { websites, selectedWebsite } = useWebsite();

  const { websiteId } = useParams<{ websiteId?: string }>();

  const hasWebsites = websites && websites.length > 0;
  

  return (
    <aside
      className={cn(
        "sticky top-16 h-[calc(100vh-4rem)] border-r bg-background transition-all duration-300",
        collapsed ? "w-16" : "w-64"
      )}
    >
      <div className="flex flex-col h-full">
        {/* Navigation */}
        <nav className="flex-1 space-y-1 p-3 overflow-y-auto">
          <TooltipProvider delayDuration={0}>
            {menuItems.map((item) => {

              if(selectedWebsite?.platform === "wix" && item.label === "Manage Plan") return

              const isDisabled = item.requiresWebsite && !websiteId;

              if (isDisabled) {
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>
                      <div
                        className={cn(
                          "flex items-center gap-3 rounded-lg px-3 py-2 transition-all cursor-not-allowed opacity-50",
                          "text-muted-foreground",
                          collapsed && "justify-center"
                        )}
                        title={collapsed ? item.label : undefined}
                      >
                        <item.icon className="h-5 w-5 shrink-0" />
                        {!collapsed && <span>{item.label}</span>}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p className="max-w-xs">
                        {!hasWebsites
                          ? "Add a website first to access this feature"
                          : "Select a website from the dropdown to access this feature"}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return (
                <NavLink
                  key={item.path}
                  to={item.requiresWebsite ? `${item.path}/${websiteId}` : item.path}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2 transition-all hover:bg-accent text-black-foreground",
                      isActive
                        ? "bg-accent"
                        : "",
                      collapsed && "justify-center"
                    )
                  }
                  title={collapsed ? `${item.label} ${websiteId}` : undefined}
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              );
            })}
          </TooltipProvider>
        </nav>

        {/* Collapse Toggle */}
        <div className="border-t p-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed(!collapsed)}
            className={cn("w-full", collapsed && "justify-center")}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <>
                <ChevronLeft className="mr-2 h-4 w-4" />
                Collapse
              </>
            )}
          </Button>
        </div>
      </div>
    </aside>
  );
}
